/* Copy da gestão de FAQ/capacidades (ADR-0658 + ADR-0720). O parágrafo informativo é quebrado em
   vários campos porque três palavras (tópico/chave de categoria/rótulo/publicados) aparecem em
   negrito no JSX — juntar tudo em uma única string perderia a marcação. */
import type { FaqStatus } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface FaqContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly newTopicButton: string;
  readonly infoIntro: string;
  readonly topicWord: string;
  readonly infoAfterTopic: string;
  readonly categoryKeyWord: string;
  readonly infoAfterCategoryKey: string;
  readonly labelWord: string;
  readonly infoAfterLabel: string;
  readonly publishedWord: string;
  readonly infoAfterPublished: string;
  readonly searchAriaLabel: string;
  readonly searchPlaceholder: string;
  readonly statusFilterAriaLabel: string;
  readonly allStatusesLabel: string;
  /** Rótulo por status (enum fixo do contrato — não deduzido dos dados). */
  readonly statusLabels: Readonly<Record<FaqStatus, string>>;
  readonly categoryFacetLabel: string;
  readonly localeFacetLabel: string;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly deleteDialogTitle: string;
  readonly deleteDialogDescription: (slug: string) => string;
  readonly deleteConfirmButton: string;
  readonly deleteSuccessToast: string;
  readonly deleteErrorToast: string;
  readonly bulk: {
    readonly selected: (count: number) => string;
    readonly remove: string;
    readonly clear: string;
    readonly title: string;
    readonly description: (count: number) => string;
    readonly success: (count: number) => string;
    readonly partial: (success: number, failed: number) => string;
  };
}

const FAQ_CONTENT: Bilingual<FaqContent> = {
  'pt-BR': {
    pageTitle: 'FAQ e capacidades',
    pageDescription: 'Perguntas e explicações de capacidades exibidas no app.',
    newTopicButton: 'Novo tópico',
    infoIntro: 'Cada linha é um ',
    topicWord: 'tópico',
    infoAfterTopic: ' com uma variação por idioma (badges). A ',
    categoryKeyWord: 'chave de categoria',
    infoAfterCategoryKey: ' é compartilhada entre os idiomas (agrupa e filtra); o ',
    labelWord: 'rótulo',
    infoAfterLabel:
      ' é traduzível por idioma. Conteúdo exibido no app (aba de configurações). Só idiomas ',
    publishedWord: 'publicados',
    infoAfterPublished:
      ' aparecem para o usuário. A lista é paginada: os filtros abaixo agem sobre os tópicos já ' +
      'carregados — use "Carregar mais" para trazer o restante. Edições ficam na trilha de auditoria.',
    searchAriaLabel: 'Buscar tópicos do FAQ',
    searchPlaceholder: 'Buscar por pergunta, slug ou categoria…',
    statusFilterAriaLabel: 'Filtrar por status',
    allStatusesLabel: 'Todos',
    statusLabels: { draft: 'Rascunho', published: 'Publicado' },
    categoryFacetLabel: 'Categoria',
    localeFacetLabel: 'Idioma',
    loadError: 'Falha ao carregar o FAQ.',
    loadMoreButton: 'Carregar mais',
    deleteDialogTitle: 'Remover tópico',
    deleteDialogDescription: (slug) =>
      `Remover "${slug}" e todas as suas traduções? Esta ação não pode ser desfeita.`,
    deleteConfirmButton: 'Remover',
    deleteSuccessToast: 'Tópico removido.',
    deleteErrorToast: 'Falha ao remover o tópico.',
    bulk: {
      selected: (count) => `${count} tópico(s) selecionado(s)`,
      remove: 'Remover selecionados',
      clear: 'Limpar seleção',
      title: 'Remover tópicos selecionados',
      description: (count) =>
        `Remover ${count} tópico(s) e todas as traduções? Esta ação não pode ser desfeita.`,
      success: (count) => `${count} tópico(s) removido(s).`,
      partial: (success, failed) => `${success} removido(s); ${failed} com falha.`,
    },
  },
  'en-US': {
    pageTitle: 'FAQ and capabilities',
    pageDescription: 'Questions and capability explanations shown in the app.',
    newTopicButton: 'New topic',
    infoIntro: 'Each row is a ',
    topicWord: 'topic',
    infoAfterTopic: ' with one variation per language (badges). The ',
    categoryKeyWord: 'category key',
    infoAfterCategoryKey: ' is shared across languages (it groups and filters); the ',
    labelWord: 'label',
    infoAfterLabel: ' is translatable per language. Content shown in the app (settings tab). Only ',
    publishedWord: 'published',
    infoAfterPublished:
      ' languages appear to the user. The list is paginated: the filters below act on the ' +
      'topics already loaded — use "Load more" to bring in the rest. Edits are tracked in the audit trail.',
    searchAriaLabel: 'Search FAQ topics',
    searchPlaceholder: 'Search by question, slug or category…',
    statusFilterAriaLabel: 'Filter by status',
    allStatusesLabel: 'All',
    statusLabels: { draft: 'Draft', published: 'Published' },
    categoryFacetLabel: 'Category',
    localeFacetLabel: 'Language',
    loadError: 'Failed to load the FAQ.',
    loadMoreButton: 'Load more',
    deleteDialogTitle: 'Remove topic',
    deleteDialogDescription: (slug) =>
      `Remove "${slug}" and all its translations? This action cannot be undone.`,
    deleteConfirmButton: 'Remove',
    deleteSuccessToast: 'Topic removed.',
    deleteErrorToast: 'Failed to remove the topic.',
    bulk: {
      selected: (count) => `${count} topic(s) selected`,
      remove: 'Remove selected',
      clear: 'Clear selection',
      title: 'Remove selected topics',
      description: (count) =>
        `Remove ${count} topic(s) and all translations? This action cannot be undone.`,
      success: (count) => `${count} topic(s) removed.`,
      partial: (success, failed) => `${success} removed; ${failed} failed.`,
    },
  },
};

export const useFaqContent = (): FaqContent => useBilingual(FAQ_CONTENT);
